import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import CmsPageLayout from "../components/CmsPageLayout";
import CmsPageRoot from "../components/CmsPageRoot";
import CmsPageShell from "../components/CmsPageShell";
import ValidationSummary from "../components/ValidationSummary";
import StickyFormActions from "../components/StickyFormActions";
import PreviewJsonModal from "../components/PreviewJsonModal";
import { CMS_LANGUAGES, CMS_PAGE_META, DEFAULT_CONTACT } from "../constants";
import { validateContactPageBilingual } from "../utils/validation";
import { applyCmsValidationFailure, scrollToFirstValidationError } from "../utils/validationUi";
import { fetchPageContent, saveContactContent, uploadMedia } from "../api/cmsApi";
import { useCmsAuth } from "../hooks/useCmsAuth";
import { MarketingHeroSection } from "../sections/SharedCmsSections";
import { ContactInfoSection, ContactMessageSection } from "../sections/ContactSections";

const PAGE_META = CMS_PAGE_META.contact;
const EN_ID = CMS_LANGUAGES.en.id;
const ES_ID = CMS_LANGUAGES.es.id;
const cloneContact = () => JSON.parse(JSON.stringify(DEFAULT_CONTACT));

const CONTACT_SECTION_MAP = [
  { id: "hero", prefixes: ["hero."] },
  { id: "infoCards", prefixes: ["infoCards."] },
  { id: "messageSection", prefixes: ["messageSection."] },
];

function ContactPageEditor() {
  const { token } = useCmsAuth();
  const [contactEn, setContactEn] = useState(() => cloneContact());
  const [contactEs, setContactEs] = useState(() => cloneContact());
  const [initialEn, setInitialEn] = useState(() => cloneContact());
  const [initialEs, setInitialEs] = useState(() => cloneContact());
  const [updatedAtEn, setUpdatedAtEn] = useState(null);
  const [updatedAtEs, setUpdatedAtEs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const dirtyEn = JSON.stringify(contactEn) !== JSON.stringify(initialEn);
  const dirtyEs = JSON.stringify(contactEs) !== JSON.stringify(initialEs);
  const dirty = dirtyEn || dirtyEs;

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErrors({});
    try {
      const [enResult, esResult] = await Promise.all([
        fetchPageContent(token, EN_ID),
        fetchPageContent(token, ES_ID),
      ]);
      const enMerged = { ...cloneContact(), ...(enResult.content.contact || {}) };
      const esMerged = { ...cloneContact(), ...(esResult.content.contact || {}) };
      setContactEn(enMerged);
      setContactEs(esMerged);
      setInitialEn(JSON.parse(JSON.stringify(enMerged)));
      setInitialEs(JSON.parse(JSON.stringify(esMerged)));
      setUpdatedAtEn(enResult.updatedAt);
      setUpdatedAtEs(esResult.updatedAt);
    } catch (err) {
      const fallback = cloneContact();
      setContactEn(fallback);
      setContactEs(cloneContact());
      setInitialEn(JSON.parse(JSON.stringify(fallback)));
      setInitialEs(JSON.parse(JSON.stringify(cloneContact())));
      toast.error("Could not load from server. Restore CMS data in MongoDB.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSave = async () => {
    const validationErrors = validateContactPageBilingual(contactEn, contactEs);
    if (Object.keys(validationErrors).length) {
      applyCmsValidationFailure({
        validationErrors,
        setErrors,
        sectionPrefixMap: CONTACT_SECTION_MAP,
        toastFn: (message) => toast.error(message),
      });
      return;
    }
    setSaving(true);
    try {
      const [enResult, esResult] = await Promise.all([
        saveContactContent(token, EN_ID, contactEn),
        saveContactContent(token, ES_ID, contactEs),
      ]);
      setInitialEn(JSON.parse(JSON.stringify(contactEn)));
      setInitialEs(JSON.parse(JSON.stringify(contactEs)));
      setErrors({});
      setUpdatedAtEn(enResult.updatedAt || new Date().toISOString());
      setUpdatedAtEs(esResult.updatedAt || new Date().toISOString());
      toast.success("Saved — live on the website (English & Spanish).");
    } catch (err) {
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (lang, key, file, onSuccess) => {
    setUploadingKey(`${lang}.${key}`);
    try {
      const publicUrl = await uploadMedia(token, file);
      if (publicUrl) {
        onSuccess(publicUrl);
        toast.success("Image uploaded.");
      } else {
        toast.error("Upload failed.");
      }
    } catch (err) {
      toast.error("Upload failed.");
    } finally {
      setUploadingKey(null);
    }
  };

  const sectionProps = {
    contactEn,
    contactEs,
    setContactEn,
    setContactEs,
    errors,
    onImageUpload: handleImageUpload,
    uploadingKey,
  };

  return (
    <CmsPageRoot>
      <CmsPageLayout
        actions={
          <StickyFormActions
            dirty={dirty}
            saving={saving}
            onSave={handleSave}
            onPreview={() => setPreviewOpen(true)}
          />
        }
      >
      <CmsPageShell
        pageIcon={PAGE_META.pageIcon}
        pageIconSrc={PAGE_META.tabIcon}
        title={PAGE_META.title}
        subtitle={PAGE_META.subtitle}
        dirtyEn={dirtyEn}
        dirtyEs={dirtyEs}
        updatedAtEn={updatedAtEn}
        updatedAtEs={updatedAtEs}
        loading={loading}
        skeletonPage="contact"
      >
        <ValidationSummary
          errors={errors}
          onJumpToFirst={() =>
            scrollToFirstValidationError(errors, { sectionPrefixMap: CONTACT_SECTION_MAP })
          }
        />
        <MarketingHeroSection
          id="hero"
          title="Hero"
          description="Top banner"
          heroEn={contactEn.hero || {}}
          heroEs={contactEs.hero || {}}
          setHeroEn={(hero) => setContactEn({ ...contactEn, hero })}
          setHeroEs={(hero) => setContactEs({ ...contactEs, hero })}
          {...sectionProps}
        />
        <ContactInfoSection {...sectionProps} />
        <ContactMessageSection {...sectionProps} />
      </CmsPageShell>
      </CmsPageLayout>
      <PreviewJsonModal
        isOpen={previewOpen}
        toggle={() => setPreviewOpen(false)}
        title="Contact page payload"
        payload={{ en: { contact: contactEn }, es: { contact: contactEs } }}
      />
    </CmsPageRoot>
  );
}

export default ContactPageEditor;
